import {
	Component,
	computed,
	inject,
	type OnDestroy,
	type OnInit,
	signal,
	ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PopoverModule, type Popover } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '../../services/toast.service';
import { PartCodingHttpService } from '../../services/part-coding-http.service';
import type { PartCodingCustomerOption } from '../../models/part-coding';
import type {
	PartCodingCandidate,
	PartCodingRow,
	PartCodingStatus
} from '../../models/part-coding.model';
import {
	buildPartCodingCsv,
	candidatesForFamily,
	DEFAULT_PASTE_TEXT,
	PART_CODING_STATUSES,
	pasteLineCount,
	rowMatchesQuery
} from './part-coding-helpers';
import {
	base64FromDataUrl,
	contentTypeFor,
	fileTooLargeMessage,
	formatFileSize,
	PART_CODING_FILE_ACCEPT,
	readFileAsDataUrl,
	unsupportedFileMessage
} from './part-coding-files';
import {
	codingStageLabels,
	CODING_SCOPES,
	fileNotices,
	fileReadingHint,
	fileReadingLabel,
	formatElapsedClock,
	scopeParentCompanyId,
	rowsFromMatch,
	sourceLabelFor,
	toCandidate,
	type CodingScope,
	type PartCodingFileNotice
} from './part-coding-rows';

@Component({
	selector: 'app-part-coding',
	standalone: true,
	imports: [
		FormsModule,
		TableModule,
		ButtonModule,
		InputTextModule,
		SelectModule,
		TagModule,
		TextareaModule,
		PopoverModule,
		TooltipModule
	],
	templateUrl: './part-coding.component.html',
	styleUrl: './part-coding.component.sass'
})
export class PartCodingComponent implements OnInit, OnDestroy {
	private readonly toastService = inject(ToastService);
	private readonly partCodingHttp = inject(PartCodingHttpService);
	private readonly msal = inject(MsalService);
	private catalogSearchTimer: ReturnType<typeof setTimeout> | null = null;
	private runStarted = 0;
	private phaseStarted = 0;
	private elapsedTimer: ReturnType<typeof setInterval> | null = null;
	private fileTimer: ReturnType<typeof setInterval> | null = null;

	public readonly fileAccept = PART_CODING_FILE_ACCEPT;

	@ViewChild('candidatePopover') private candidatePopover?: Popover;

	public readonly customers = signal<PartCodingCustomerOption[]>([]);
	public readonly customersLoading = signal(false);
	public readonly codingScopes = CODING_SCOPES.map((scope) => ({
		id: scope.id,
		label: scope.label
	}));
	public readonly statuses: PartCodingStatus[] = [...PART_CODING_STATUSES];

	public readonly pasteText = signal(DEFAULT_PASTE_TEXT);
	public readonly rows = signal<PartCodingRow[]>([]);
	public readonly isCoding = signal(false);
	public readonly stageLabel = signal('Parsing the quote…');
	public readonly elapsedMs = signal(0);
	public readonly doneMs = signal<number | null>(null);
	public readonly phaseTimings = signal<
		readonly { name: string; milliseconds: number }[]
	>([]);
	public readonly pendingFile = signal<File | null>(null);
	public readonly readingFile = signal(false);
	public readonly fileElapsedSeconds = signal(0);
	public readonly uploadNotices = signal<readonly PartCodingFileNotice[]>([]);
	public readonly searchQuery = signal('');
	public readonly statusFilter = signal<ReadonlySet<PartCodingStatus>>(
		new Set()
	);
	public readonly signedInUser = signal(this.accountName());
	public readonly selectedCustomerId = signal<string | null>(null);
	public readonly selectedCustomer = computed(
		() =>
			this.customers().find(
				(customer) => customer.id === this.selectedCustomerId()
			) ?? null
	);
	public readonly selectedScope = signal<CodingScope>('generic');
	public readonly batchId = signal<number | null>(null);

	public readonly activeRow = signal<PartCodingRow | null>(null);
	public readonly candidateFilter = signal('');
	public readonly catalogCandidates = signal<PartCodingCandidate[] | null>(
		null
	);
	public readonly selectedCandidateCode = signal<string | null>(null);
	public readonly reasonText = signal('');

	public readonly lineCount = computed(() =>
		pasteLineCount(this.pasteText())
	);

	public readonly searchedRows = computed(() =>
		this.rows().filter((row) => rowMatchesQuery(row, this.searchQuery()))
	);

	public readonly filteredRows = computed(() => {
		const filter = this.statusFilter();
		const rows = this.searchedRows();
		return filter.size
			? rows.filter((row) => filter.has(row.status))
			: rows;
	});

	public readonly statusCounts = computed(() => {
		const rows = this.searchedRows();
		const counts: Record<PartCodingStatus, number> = {
			Auto: 0,
			Manual: 0,
			Review: 0,
			'Out of Stock': 0,
			RFQ: 0
		};
		for (const row of rows) {
			counts[row.status]++;
		}
		return counts;
	});

	public readonly sourceLabel = computed(() => sourceLabelFor(this.rows()));

	public readonly phaseTooltip = computed(() =>
		this.phaseTimings()
			.map(
				(phase) => `${phase.name} ${this.formatMs(phase.milliseconds)}`
			)
			.join('\n')
	);

	public readonly canCode = computed(
		() => this.pasteText().trim().length > 0 || this.pendingFile() !== null
	);

	public readonly longFileHint = computed(() =>
		this.readingFile() ? fileReadingHint(this.fileElapsedSeconds()) : null
	);

	public readonly showingLabel = computed(() => {
		const total = this.rows().length;
		if (!total) return '';
		const showing = this.filteredRows().length;
		return showing === total
			? `${total} lines`
			: `showing ${showing} of ${total} lines`;
	});

	public readonly candidates = computed<readonly PartCodingCandidate[]>(
		() => {
			const row = this.activeRow();
			if (!row) return [];
			const searched = this.catalogCandidates();
			if (searched) return searched;
			const all = row.candidates.length
				? row.candidates
				: candidatesForFamily(row.candidateFamily);
			const filter = this.candidateFilter().trim().toLowerCase();
			if (!filter) return all;
			return all.filter((candidate) =>
				(candidate.code + ' ' + candidate.description)
					.toLowerCase()
					.includes(filter)
			);
		}
	);

	public ngOnInit(): void {
		this.customersLoading.set(true);
		this.partCodingHttp.customers().subscribe({
			next: (customers) => {
				this.customers.set(customers);
				this.customersLoading.set(false);
			},
			error: () => {
				this.customersLoading.set(false);
				this.toastService.showErrorToast('Could not load customers.');
			}
		});
	}

	public onCustomerChange(id: string | null): void {
		this.selectedCustomerId.set(id);
		if (id) this.selectedScope.set('customer');
	}

	public ngOnDestroy(): void {
		this.stopElapsed();
		this.stopFileClock();
		if (this.catalogSearchTimer) clearTimeout(this.catalogSearchTimer);
	}

	public onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (!file || this.isCoding()) return;

		const unsupported = unsupportedFileMessage(file.name);
		if (unsupported) {
			this.toastService.showErrorToast(unsupported);
			return;
		}
		const tooLarge = fileTooLargeMessage(file.size);
		if (tooLarge) {
			this.toastService.showErrorToast(tooLarge);
			return;
		}

		this.pendingFile.set(file);
		this.uploadNotices.set([]);
		this.rows.set([]);
		this.statusFilter.set(new Set());
	}

	public clearPendingFile(): void {
		if (this.isCoding()) return;
		this.pendingFile.set(null);
	}

	public fileSizeLabel(bytes: number): string {
		return formatFileSize(bytes);
	}

	public dismissUploadNotice(index: number): void {
		this.uploadNotices.update((notices) =>
			notices.filter((_, noticeIndex) => noticeIndex !== index)
		);
	}

	public async codeIt(): Promise<void> {
		if (!this.selectedCustomer()) {
			this.toastService.showErrorToast('Select a customer first.');
			return;
		}

		const file = this.pendingFile();
		const body = this.pasteText().trim();
		if (!body && !file) {
			this.toastService.showErrorToast(
				'Paste customer lines or attach a file first.'
			);
			return;
		}

		this.stopElapsed();
		this.stopFileClock();
		this.phaseTimings.set([]);
		this.doneMs.set(null);
		this.uploadNotices.set([]);
		this.readingFile.set(file !== null);
		this.stageLabel.set(
			file
				? fileReadingLabel(file.name)
				: (codingStageLabels(this.selectedScope())[0] ??
						'Parsing the quote…')
		);
		this.isCoding.set(true);
		if (file) this.startFileClock();
		else this.startElapsed();

		let succeeded = false;
		try {
			const attachments = file
				? [
						{
							name: file.name,
							contentType: contentTypeFor(file.name, file.type),
							contentBytes: base64FromDataUrl(
								await readFileAsDataUrl(file)
							)
						}
					]
				: [];
			const coded = await this.partCodingHttp.codeBatchLive(
				{
					body: body || null,
					attachments,
					parentCompanyId: scopeParentCompanyId(
						this.selectedCustomer()?.parentId ?? null,
						this.selectedScope()
					),
					historyParentCompanyId:
						this.selectedCustomer()?.parentId ?? null,
					locationId: this.selectedCustomer()?.locationId ?? null,
					locationName: this.selectedCustomer()?.locationName ?? null
				},
				(phase) => {
					if (this.readingFile()) return;
					this.onPhaseFinished(phase.name);
				}
			);
			this.phaseTimings.set(coded.timings);
			this.uploadNotices.set(fileNotices(coded.attachments));
			if (coded.extractionFailed) {
				this.toastService.showErrorToast(
					coded.extractionError ??
						'Could not read line items from this text.'
				);
				return;
			}
			const matched = coded.match;
			if (!matched || matched.rows.length === 0) {
				this.rows.set([]);
				this.toastService.showErrorToast(
					'No line items were found in that text.'
				);
				return;
			}
			if (matched.basketUnavailable) {
				this.toastService.showErrorToast(
					matched.basketError ??
						'That customer’s market basket could not be loaded.'
				);
			}

			const rows = rowsFromMatch(matched);
			this.rows.set(rows);
			this.batchId.set(coded.batchId);
			this.statusFilter.set(new Set());
			this.toastService.showSuccessToast(this.codedSummary(rows));
			succeeded = true;
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : '';
			const serverMessage =
				message && message !== 'Coding failed.' ? message : '';
			this.toastService.showErrorToast(
				serverMessage ||
					(file
						? 'Coding failed. The coding service did not respond. If the file is very long, try splitting it into smaller files.'
						: 'Coding failed. The coding service did not respond.')
			);
		} finally {
			if (succeeded) this.pendingFile.set(null);
			this.doneMs.set(Math.round(performance.now() - this.runStarted));
			this.stopElapsed();
			this.stopFileClock();
			this.readingFile.set(false);
			this.isCoding.set(false);
		}
	}

	private onPhaseFinished(completed: string): void {
		const labels = codingStageLabels(this.selectedScope());
		const history = labels[1] ?? 'Searching historical matches…';
		const basket = labels.length > 3 ? (labels[2] ?? null) : null;
		const itemMaster =
			labels[labels.length - 1] ?? 'Searching the item master…';
		let next = this.stageLabel();
		if (completed === 'Parse') next = history;
		else if (completed === 'History') next = basket ?? itemMaster;
		else if (completed === 'Basket score' || completed === 'Item master')
			next = itemMaster;
		if (next === this.stageLabel()) return;
		this.stageLabel.set(next);
		this.phaseStarted = performance.now();
		this.elapsedMs.set(0);
	}

	public formatMs(milliseconds: number): string {
		if (milliseconds >= 1000) return `${(milliseconds / 1000).toFixed(1)}s`;
		return `${milliseconds}ms`;
	}

	public fileClock(): string {
		return formatElapsedClock(this.fileElapsedSeconds());
	}

	private startElapsed(): void {
		this.runStarted = this.phaseStarted = performance.now();
		this.elapsedMs.set(0);
		this.elapsedTimer = setInterval(() => {
			this.elapsedMs.set(
				Math.round(performance.now() - this.phaseStarted)
			);
		}, 100);
	}

	private stopElapsed(): void {
		if (!this.elapsedTimer) return;
		clearInterval(this.elapsedTimer);
		this.elapsedTimer = null;
	}

	private startFileClock(): void {
		this.runStarted = performance.now();
		this.fileElapsedSeconds.set(0);
		this.fileTimer = setInterval(() => {
			this.fileElapsedSeconds.set(
				Math.floor((performance.now() - this.runStarted) / 1000)
			);
		}, 1000);
	}

	private stopFileClock(): void {
		if (!this.fileTimer) return;
		clearInterval(this.fileTimer);
		this.fileTimer = null;
	}

	private codedSummary(rows: readonly PartCodingRow[]): string {
		const count = (status: PartCodingStatus): number =>
			rows.filter((row) => row.status === status).length;
		const outOfStock = count('Out of Stock');
		return (
			`Coded ${rows.length} lines — ${count('Auto')} auto, ` +
			`${count('Review')} review, ${count('RFQ')} to RFQ` +
			(outOfStock ? `, ${outOfStock} out of stock` : '')
		);
	}

	private accountName(): string {
		const account = this.msal.instance.getActiveAccount();
		return account?.name ?? account?.username ?? 'Signed in';
	}

	public toggleStatusFilter(status: PartCodingStatus): void {
		const next = new Set(this.statusFilter());
		if (next.has(status)) {
			next.delete(status);
		} else {
			next.add(status);
		}
		this.statusFilter.set(next);
	}

	public clearFilters(): void {
		this.statusFilter.set(new Set());
		this.searchQuery.set('');
	}

	public onCandidateFilter(value: string): void {
		this.candidateFilter.set(value);
		if (this.catalogSearchTimer) clearTimeout(this.catalogSearchTimer);
		const query = value.trim();
		if (query.length < 2) {
			this.catalogCandidates.set(null);
			return;
		}

		this.catalogSearchTimer = setTimeout(() => {
			void this.searchCatalog(query);
		}, 300);
	}

	public openCandidatePopover(event: Event, row: PartCodingRow): void {
		this.activeRow.set(row);
		this.candidateFilter.set('');
		this.catalogCandidates.set(null);
		this.selectedCandidateCode.set(null);
		this.reasonText.set(
			row.reason || (row.status === 'Out of Stock' ? 'Out of stock' : '')
		);
		this.candidatePopover?.toggle(event);
	}

	private async searchCatalog(query: string): Promise<void> {
		try {
			const result = await firstValueFrom(
				this.partCodingHttp.search({
					query,
					parentCompanyId: scopeParentCompanyId(
						this.selectedCustomer()?.parentId ?? null,
						this.selectedScope()
					)
				})
			);
			if (this.candidateFilter().trim() !== query) return;
			if (result.basketUnavailable) {
				this.catalogCandidates.set(null);
				this.toastService.showErrorToast(
					result.basketError ??
						'That customer’s market basket could not be loaded.'
				);
				return;
			}
			this.catalogCandidates.set(result.candidates.map(toCandidate));
		} catch {
			if (this.candidateFilter().trim() !== query) return;
			this.catalogCandidates.set(null);
			this.toastService.showErrorToast('Catalog search failed.');
		}
	}

	public closeCandidatePopover(): void {
		this.candidatePopover?.hide();
		this.activeRow.set(null);
	}

	public confirmCandidate(): void {
		const row = this.activeRow();
		const code = this.selectedCandidateCode();
		if (!row || !code) {
			this.toastService.showErrorToast('Pick a candidate first.');
			return;
		}

		const candidate = this.candidates().find((c) => c.code === code);
		if (!candidate) return;

		const wasOutOfStock = row.status === 'Out of Stock';
		const oldCode = row.partCode;
		const pickHasNoStock = candidate.inventory === 0;

		// 0 on hand is Out of Stock no matter who picked it or how confident
		// the match is. And once a line has ever been Out of Stock it stays
		// flagged that way even after being resolved to an in-stock
		// alternate, so it can still be filtered on as "shipped short at
		// some point" instead of disappearing into Manual.
		const samePart = candidate.code === row.partCode;
		const nextStatus: PartCodingStatus =
			wasOutOfStock || pickHasNoStock
				? 'Out of Stock'
				: samePart
					? row.status
					: 'Manual';
		const repCheck = samePart ? 'accepted' : 'changed';

		const typedReason = this.reasonText().trim();
		let reason: string;
		if (wasOutOfStock && !pickHasNoStock) {
			const audit = `Out of stock (${oldCode}) — swapped to ${candidate.code}.`;
			reason =
				typedReason && typedReason !== 'Out of stock'
					? `${typedReason} — ${audit}`
					: audit;
		} else if (nextStatus === 'Out of Stock') {
			reason = typedReason || 'Out of stock';
		} else {
			reason = typedReason;
		}

		this.rows.update((rows) =>
			rows.map((r) =>
				r.id === row.id
					? {
							...r,
							partCode: candidate.code,
							rkDescription: candidate.description,
							inventory: candidate.inventory,
							isMarketBasket: candidate.isMarketBasket,
							confidence: candidate.matchPercent,
							status: nextStatus,
							source: samePart ? row.source : 'Manual',
							repCheck,
							reason
						}
					: r
			)
		);

		this.toastService.showSuccessToast(
			pickHasNoStock
				? `Marked Out of Stock — ${candidate.code}`
				: wasOutOfStock
					? `Swapped to ${candidate.code} — stays flagged Out of Stock for audit`
					: `Marked Manual — ${candidate.code}`
		);
		this.closeCandidatePopover();
	}

	public async exportRows(): Promise<void> {
		if (!this.rows().length) {
			this.toastService.showErrorToast(
				'Nothing to export — code the lines first.'
			);
			return;
		}
		const rows = this.filteredRows();
		if (!rows.length) {
			this.toastService.showErrorToast(
				'No lines match the current filters to export.'
			);
			return;
		}
		const batchId = this.batchId();
		if (batchId == null || rows.some((row) => row.codingLineId == null)) {
			this.toastService.showErrorToast(
				'Code the lines again before export.'
			);
			return;
		}

		const forAdmin = rows.filter((row) => !this.staysOffAdminQueue(row));
		const exactCount = rows.length - forAdmin.length;
		if (forAdmin.length) {
			try {
				await firstValueFrom(
					this.partCodingHttp.exportBatch(batchId, forAdmin)
				);
			} catch {
				this.toastService.showErrorToast(
					'Could not send these lines to the admin queue.'
				);
				return;
			}
		}

		this.downloadCsv(rows);
		const lineWord = rows.length === 1 ? 'line' : 'lines';
		const exactWord = exactCount === 1 ? 'match' : 'matches';
		this.toastService.showSuccessToast(
			exactCount
				? `Exported ${rows.length} ${lineWord} to CSV. ${exactCount} exact ${exactWord} stayed off the admin queue.`
				: `Exported ${rows.length} ${lineWord} to CSV and sent them to the admin queue.`
		);
	}

	private staysOffAdminQueue(row: PartCodingRow): boolean {
		return row.confidence >= 100 && row.source !== 'Manual';
	}

	public initials(name: string): string {
		return name
			.split(/\s+/)
			.filter(Boolean)
			.map((word) => word[0])
			.join('')
			.slice(0, 2)
			.toUpperCase();
	}

	public statusSeverity(
		status: PartCodingStatus
	): 'success' | 'info' | 'warn' | 'danger' | 'contrast' {
		switch (status) {
			case 'Auto':
				return 'success';
			case 'Manual':
				return 'info';
			case 'Review':
				return 'warn';
			case 'Out of Stock':
				return 'contrast';
			case 'RFQ':
				return 'danger';
		}
	}

	public confidenceSeverity(
		confidence: number
	): 'success' | 'warn' | 'danger' {
		if (confidence >= 90) return 'success';
		if (confidence >= 70) return 'warn';
		return 'danger';
	}

	public marketBasketSeverity(value: boolean): 'success' | 'danger' {
		return value ? 'success' : 'danger';
	}

	private downloadCsv(rows: readonly PartCodingRow[]): void {
		const submittedAt = new Date();
		const csv = buildPartCodingCsv(rows, this.signedInUser(), submittedAt);
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `part_coding_${new Date().toISOString().slice(0, 10)}.csv`;
		anchor.click();
		URL.revokeObjectURL(url);
		this.removeRows(rows);
	}

	private removeRows(rows: readonly PartCodingRow[]): void {
		if (!rows.length) return;
		const ids = new Set(rows.map((row) => row.id));
		this.rows.update((all) => all.filter((row) => !ids.has(row.id)));
		this.statusFilter.set(new Set());
		this.searchQuery.set('');
	}
}
